import { Input } from '@latitude/input';
import { Content, Root, Trigger } from '@latitude/popover';
import { Calendar } from 'components/Calendar';
import type { CalendarProps } from 'components/ComponentsTypes';
import { ErrorMessage } from 'components/ErrorMessage';
import useDatepickerContext from 'hooks/useDatepickerContext';
import { useId, useMemo } from 'react';
import type {
  DateRange,
  DayPickerContextValue,
  DayPickerProps,
} from 'react-day-picker';
import { DayPickerProvider } from 'react-day-picker';
import { classesFor, sizes } from 'utils/designSystemTokens';

type DatepickerWrapperProps = {
  /**
   * Whether the DatePicker is required or not.
   */
  required: boolean;
  /**
   * Datepicker trigger component
   */
  children?: React.ReactNode;
};

export const DatepickerWrapper = ({
  required,
  children,
}: DatepickerWrapperProps) => {
  const {
    open,
    mode,
    minDate,
    maxDate,
    format,
    value,
    inputProps,
    onOpenChange,
    onDayClick,
    inputState,
  } = useDatepickerContext();

  const errorMessageId = useId();

  const initialProviderProps = {
    styles: {},
    mode,
    required,
    captionLayout: 'dropdown-buttons',
    numberOfMonths: 1,
    format,
    fromDate: minDate,
    toDate: maxDate,
    //*...dayPickerProps,
    //*...props,
    selected: value,
    onDayClick: (day: Date) => {
      onDayClick(day);
    },
  } as Partial<DayPickerContextValue>;

  const defaultMonth = useMemo(() => {
    if (!value) {
      return new Date();
    }
    if (mode === 'range') {
      return (value as DateRange).from as Date;
    }
    return value as Date;
  }, [mode, value]);

  return (
    <DayPickerProvider initialProps={initialProviderProps as DayPickerProps}>
      <div className="relative">
        <Root open={open} onOpenChange={onOpenChange}>
          {/* /*
              If we dont assign the trigger asChild, it will render as a button, and we cannot have a button
            inside of a button but we have fixed buttons inside of the input.
              If we set the asChild, it will render as an Anchor, but with a default type of "button", that
            also causes the input to behave in a weird way, for example, the placeholder stops working
              So we need to set the type to undefined to override that behaviour.
              https://github.com/radix-ui/primitives/issues/1461
            */}
          <Trigger asChild type={undefined}>
            {children || (
              <span>
                <Input
                  {...inputProps}
                  aria-describedby={
                    inputState.hasError ? errorMessageId : undefined
                  }
                  aria-invalid={inputState.hasError}
                />
              </span>
            )}
          </Trigger>
          <Content
            align="start"
            data-testid="datepicker-popover"
            className={`${classesFor(sizes.popover)} ${sizes.popoverHeight[mode]}`}
          >
            <Calendar
              initialFocus={false}
              defaultMonth={defaultMonth}
              {...(initialProviderProps as CalendarProps)}
            />
          </Content>
        </Root>

        {/* Error message display */}
        <ErrorMessage
          id={errorMessageId}
          show={inputState.hasError}
          message={inputState.errorMessage}
        />
      </div>
    </DayPickerProvider>
  );
};
